import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MailScreen from '../screens/Mail';
import { withPusher } from '../contexts/PusherContext';
import { PUSHER } from '../config/constants';
import { localMessageConversations } from '../utils/storedLocalData';
import _isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import useMixPanel from '../hooks/useMixPanel';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PWAMessagePageBanner from '../components/pwa/PWAMessagePageBanner';
import { showPWAInstallationMessagePageBanner } from '../utils/pwa';
import { AccountMaker } from '../models/Account';
import { mixpanelTrackPushNotifClickedAfterRedirection } from '../utils/mixpanel/pushNotifClicked';
import { RootState } from '../store';
import { getGlobalNotifications } from '../actions/globalNotificationActions';
import {
  enableConversation,
  fetchReceiverThread,
  fetchThreadTimeout,
  handleUnlockOffer,
  presetOrder,
  setConversationPage,
  setThread,
  toggleAcctTyping,
  triggerReplaceMessageId,
  triggerThreadsFetching,
  updateMessageandConvo,
  updateMessages,
  updateThreadsCollection,
} from '../actions/mailActions';
import { endPendingUpload } from '../actions/profileActions';
import { EVENT_NAMES } from '../constants/mixpanel';
import useMailFetchActions from '../modules/mail/hooks/useMailFetchActions';
import { getBrowserDetails, isQAautomationEmail, tabletWidth } from '../utils/helpers';
import MasterConfig from 'config/Master';

// Helper: Map page types from path
const PAGE_TYPES = ['archive', 'unlocked', 'locked', 'unread'] as const;

const getPage = (path: string): string => {
  const pageType = PAGE_TYPES.find(type => path.includes(type));

  return pageType || 'recent';
};

// Helper: Check if hashId matches thread/account identifiers
const isMatchingHashId = (hashId: string | undefined, data: any): boolean => {
  if (!hashId || !data) return false;
  return hashId === data.account?.hash_id || hashId === data.thread?.uid || hashId === data.extras?.thread_uid;
};

// Helper: Log Pusher events in non-production
const logPusherEvent = (eventName: string, data: any): void => {
  if (MasterConfig.APP_ENV !== 'production') {
    console.log(`Notification Received from Pusher for event - ${eventName}`, data);
  }
};

// Helper: Get conversation from local storage
const getLocalConversation = async (hashId: string | undefined) => {
  if (!hashId) return {};
  const conversationMessage = await localMessageConversations(hashId);

  return _isEmpty(conversationMessage) ? {} : conversationMessage;
};

const MessagesContainer = ({ bindListener, onRealtimeEvent, stopListening }) => {
  const { getThreadsCancelTokenSource, fetchThreads, fetchConversation } = useMailFetchActions();

  const account = useSelector((state: RootState) => state.profile);
  const isUploading = useSelector((state: RootState) => state.profile.pendingUploads);
  const threads = useSelector((state: RootState) => state.mail.threads);
  const conversation = useSelector((state: RootState) => state.mail.conversation);
  const order = useSelector((state: RootState) => state.mail.order);

  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackPackagesPageVisited, mixpanelData, resetMixpanelData } = useMixPanel();

  const [showPwaInstallPromptBanner, setShowPwaInstallPromptBanner] = useState(false);

  useEffect(() => {
    (async () => {
      const showBanner = await showPWAInstallationMessagePageBanner(AccountMaker.create(account));

      setShowPwaInstallPromptBanner(showBanner.show);
    })();
  }, [account]);

  useEffect(() => {
    dispatch(presetOrder(getPage(location.pathname)));
    dispatch(endPendingUpload(false));
    setTimeout(() => checkUnlock(), 1500);

    return () => {
      dispatch(getGlobalNotifications());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bindListener(listen);
    return () => {
      stopListening(listen);
    };
  }, [threads, conversation, account]);

  useEffect(() => {
    if (order) {
      fetchThreads(1);
    }

    return () => {
      getThreadsCancelTokenSource.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    if (params.hashId) {
      fetchConversation();
    }
  }, [params.hashId]);

  useEffect(() => {
    const isMobileOrTablet = !getBrowserDetails().isMobile || (getBrowserDetails().isMobile && tabletWidth);

    if (location.pathname === '/mail' && isMobileOrTablet) {
      if (threads?.collection?.length > 0) {
        const firstThread = threads.collection[0];
        const threadHashId = firstThread.uid ?? firstThread.other_profile?.data?.hash_id;

        if (threadHashId) {
          navigate(`${location.pathname}/${threadHashId}`, { replace: true });
          return;
        }
      }
    }

    dispatch(setConversationPage(getPage(location.pathname), params.hashId));
  }, [params, location.pathname]);

  useEffect(() => {
    dispatch(triggerReplaceMessageId());
  }, [isUploading]);

  useEffect(() => {
    if (location) {
      mixpanelTrackPushNotifClickedAfterRedirection(location);
    }
  }, [location]);

  const checkUnlock = async () => {
    const currentHashId = location.pathname.split('/')[2];
    const currentConversation = await getLocalConversation(currentHashId);
    const queryStringParsed = queryString.parse(location.search);
    const unlockMsg = queryStringParsed?.unlockMessage === 'true';

    if (unlockMsg && currentConversation.locked) {
      handleUnlockFromFav();
    }
  };

  const listen = () => {
    const { EVENTS } = PUSHER;

    // Handle account alerts (unlock events)
    onRealtimeEvent(EVENTS.ACCOUNT_ALERTS, data => {
      logPusherEvent(EVENTS.ACCOUNT_ALERTS, data);

      if (data.type !== 'mail' || data.action !== 'unlock') {
        return false;
      }

      const { data: realtimeData } = data;
      const senderHashId = realtimeData.account.hash_id;

      if (
        conversation.hash_id === senderHashId ||
        (conversation.to_account_hash_id === senderHashId && data.action === 'unlock')
      ) {
        dispatch(enableConversation());
      }

      dispatch(updateThreadsCollection(senderHashId)).then(() => {
        dispatch(fetchThreadTimeout(navigate, location));
      });
    });

    // Handle new messages
    onRealtimeEvent(EVENTS.ACCOUNT_ALERT_MESSAGE, ({ data }) => {
      logPusherEvent(EVENTS.ACCOUNT_ALERT_MESSAGE, data);

      const { message, thread } = data;
      const messageData = conversation.messages?.data || [];
      const isConversationPath = window.location.pathname === `/mail/${thread.uid ?? message.hash_id}`;

      if (isConversationPath) {
        message.i_read = true;
      }

      const lastMessage = messageData[messageData.length - 1];
      const shouldUpdateLastMessage = lastMessage && message.uid > lastMessage.uid;

      const newThread = {
        ...threads,
        collection: shouldUpdateLastMessage
          ? threads.collection.map(item =>
              item.uid === thread.uid ? { ...item, last_message: { data: message } } : item
            )
          : threads.collection,
      };

      dispatch(setThread(newThread)).then(() => {
        dispatch(fetchReceiverThread());
        fetchConversation();
      });

      if (conversation.uid !== thread.uid) {
        return false;
      }

      const messages = conversation.messages ? { ...conversation.messages } : { data: [] };
      const msgIdx = messages.data.findIndex(msg => msg.uid === message.uid && message.status === 'deleted');

      if (msgIdx < 0) {
        messages.data.push(message);
      } else {
        messages.data[msgIdx] = message;
      }

      dispatch(updateMessages(messages));

      const conversationBody = document.getElementById('conversation_body');

      if (conversationBody) {
        conversationBody.scrollTop = conversationBody.scrollHeight;
      }
    });

    // Handle typing indicator
    onRealtimeEvent(EVENTS.ACCOUNT_TYPING_INDICATOR, ({ data }) => {
      logPusherEvent(EVENTS.ACCOUNT_TYPING_INDICATOR, data);

      if (isMatchingHashId(params.hashId, data)) {
        dispatch(toggleAcctTyping());
        setTimeout(() => dispatch(toggleAcctTyping()), isQAautomationEmail(account.email) ? 6000 : 1000);
      }
    });

    // Handle message status updates
    onRealtimeEvent(EVENTS.ACCOUNT_MESSAGE_STATUS, ({ data }) => {
      logPusherEvent(EVENTS.ACCOUNT_MESSAGE_STATUS, data);

      if (isMatchingHashId(params.hashId, data)) {
        dispatch(updateMessageandConvo());
      }
    });
  };

  const handleUnlockOfferCallback = (): void => {
    dispatch(triggerThreadsFetching(1, navigate, location, null));
    resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
  };

  const handleUnlockFromFav = async () => {
    try {
      const currentHashId = location.pathname.split('/')[2];
      const currentConversation = await getLocalConversation(currentHashId);

      if (!currentConversation || !currentConversation.locked) {
        return;
      }

      if (currentConversation.offer?.data?.credit_cost > account.credits) {
        trackPackagesPageVisited({ Source: 'Unlock Attempt' });
        navigate('/packages');
        return;
      }

      setTimeout(() => {
        dispatch(
          handleUnlockOffer(
            currentConversation,
            currentConversation.uid ?? currentConversation.id,
            currentConversation.other_profile.data.hash_id,
            params,
            navigate,
            location,
            mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy,
            handleUnlockOfferCallback
          )
        );
      }, 500);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {typeof params.hashId === 'undefined' && (
        <PWAMessagePageBanner onClose={() => setShowPwaInstallPromptBanner(false)} />
      )}
      <MailScreen reminderStatus={false} showPwaInstallPromptBanner={showPwaInstallPromptBanner} />
    </div>
  );
};

export default withPusher(MessagesContainer);
